import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  getPendingUpgrade,
  clearPendingUpgrade,
  setLastUpgradeIntent,
} from "@/lib/pendingUpgrade";
import { trackEvent, trackEventAndThen } from "@/lib/analytics";

/**
 * Watches auth state. When a user becomes authenticated AND a pending
 * upgrade intent exists in localStorage (set pre-auth from the export
 * paywall), auto-invokes create-checkout and redirects to Stripe.
 *
 * Hardening: the pending intent is cleared ONLY after a successful
 * checkout invocation (we have data.url and are redirecting). On
 * transient failure the intent is preserved within its TTL so the
 * next app load or manual retry can resume.
 */
export function useResumeCheckout() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (loading || !user) return;
    if (inFlightRef.current) return;

    const pending = getPendingUpgrade();
    if (!pending) return;

    inFlightRef.current = true;

    (async () => {
      const source = "resume_after_login";
      const plan = pending.plan;
      const intentProps: Record<string, string> = {};
      if (pending.tool_type) intentProps.tool_type = pending.tool_type;
      if (pending.source_page) intentProps.source_page = pending.source_page;
      if (pending.paywall_copy_variant) intentProps.paywall_copy_variant = pending.paywall_copy_variant;
      trackEvent("upgrade_intent_resumed", { ...intentProps, plan });
      toast({
        title: "Resuming your upgrade…",
        description: "Redirecting to secure checkout.",
      });

      trackEvent("checkout_create_attempt", { ...intentProps, plan, source });
      let errorTracked = false;
      try {
        const { data, error } = await supabase.functions.invoke(
          "create-checkout",
          { body: { priceType: plan } },
        );
        if (error) {
          trackEvent("checkout_create_error", {
            ...intentProps,
            plan,
            source,
            error_type: "function_error",
          });
          errorTracked = true;
          throw error;
        }
        if (!data?.url) {
          trackEvent("checkout_create_error", {
            ...intentProps,
            plan,
            source,
            error_type: "no_url",
          });
          errorTracked = true;
          throw new Error("No checkout URL returned");
        }

        // Success: clear intent only now, then redirect.
        trackEvent("checkout_create_success", { ...intentProps, plan, source });
        clearPendingUpgrade();
        const url = data.url;
        setLastUpgradeIntent(plan, intentProps);
        trackEventAndThen("checkout_redirect", { ...intentProps, plan, source }, () => {
          window.location.href = url;
        });
      } catch (err) {
        console.error("Resume checkout failed:", err);
        if (!errorTracked) {
          trackEvent("checkout_create_error", {
            ...intentProps,
            plan,
            source,
            error_type: "exception",
          });
        }
        // Preserve pending intent for retry within TTL.
        toast({
          title: "Couldn't resume upgrade",
          description: "Try again from the Pricing page.",
          variant: "destructive",
        });
        // Release the guard so a subsequent auth refresh can re-attempt.
        inFlightRef.current = false;
        navigate("/pricing");
      }
    })();
  }, [user, loading, navigate, toast]);
}